import React, { useEffect, useMemo, useState } from "react";
import ContentLayout from "../components/ContentLayout";
import Button from "../components/Button";
import { CircularProgress, Icon } from "@material-ui/core";
import styled from "styled-components";
import { RouteComponentProps } from "react-router";
import { getReportViewData } from "./service";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import {
  GridColumn,
  GridDataStateChangeEvent,
} from "@progress/kendo-react-grid";
import { DataResult, process, State } from "@progress/kendo-data-query";
import UnstyledGrid from "../components/Grid";
import HeaderCell from "../components/Grid/HeaderCell";
import ColumnMenu from "../components/Grid/ColumnMenu";
import useDataLoader from "hooks/useDataLoader";
import ReportViewData from "./interfaces/ReportViewData";
import exportToExcel from "../components/Grid/exportToExcel";
import { convertIntegerToGUID } from "helpers/guid";
import { connect } from "react-redux";
import LoadingScreen from "../components/LoadingScreen";

const Main = styled.div`
  padding: 30px 40px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  font-family: Inter, system-ui;
  font-size: 22px;
  font-weight: 400;
  line-height: 24px;
  color: #5f6368;
  text-align: center;
  flex: 1;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: right;
  margin-bottom: 30px;
`;

const FixedWidthButton = styled(Button)`
  width: 100px;
`;

const Grid = styled(UnstyledGrid)`
  flex-grow: 1;
  height: 0;
`;

const initState = { take: 100, skip: 0 };

interface ViewReportProps extends RouteComponentProps {
  authentication: { user?: { id: number } };
}

const ViewReport = ({ history, match, authentication }: ViewReportProps) => {
  // @ts-ignore
  const reportId = match.params.selfServiceReportId;

  const getViewDataFn = () => getReportViewData(reportId);
  const { isLoading, data: viewData } =
    useDataLoader<ReportViewData>(getViewDataFn);

  const columns = viewData?.reportData.columns ?? [];
  const dataSource = viewData?.reportData.dataSource ?? [];

  const [dataState, setDataState] = useState<State>(initState);
  const [result, setResult] = useState<DataResult>({ data: [], total: 0 });
  const [isExporting, setIsExporting] = useState<boolean>(false);

  useEffect(() => {
    if (viewData) {
      const newState = {
        ...initState,
        ...viewData.report.tableSettings.dataState,
      };
      setDataState(newState);

      const newResult = process(
        viewData.reportData.dataSource.slice(0),
        newState
      );
      setResult(newResult);
    }
  }, [viewData]);

  const isReportOwner = useMemo(
    () =>
      authentication.user
        ? viewData?.report.userId ===
          convertIntegerToGUID(authentication.user.id)
        : false,
    [viewData]
  );

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
    setResult(process(dataSource.slice(0), event.dataState));
  };

  const exportRef = React.useRef<ExcelExport | null>(null);

  const exportData = () => {
    setIsExporting(true);
    if (exportRef.current !== null) {
      const exportResult = process(dataSource.slice(0), {
        filter: dataState.filter,
        sort: dataState.sort,
      });
      exportToExcel(exportResult, exportRef.current, columns);
    }
  };

  return (
    <ContentLayout
      leftColumn={
        <Button variant="text" color="primary" onClick={() => history.goBack()}>
          <Icon>arrow_back</Icon>Back
        </Button>
      }
      centerColumn={<Title>{viewData?.report.name}</Title>}
    >
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <Main>
          <Toolbar>
            <Button color="primary" onClick={exportData} disabled={isExporting}>
              {isExporting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Export"
              )}
            </Button>
            {isReportOwner && (
              <>
                <FixedWidthButton
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: "20px" }}
                  onClick={() =>
                    history.push(
                      `/reporting/self-service-reports/edit/${reportId}`
                    )
                  }
                >
                  Edit
                </FixedWidthButton>
              </>
            )}
          </Toolbar>
          <ExcelExport
            ref={exportRef}
            onExportComplete={() => setIsExporting(false)}
          >
            <Grid
              data={result}
              {...dataState}
              onDataStateChange={dataStateChange}
              sortable
              pageable
            >
              {columns.map((col, index) => (
                <GridColumn
                  key={index}
                  {...col}
                  columnMenu={ColumnMenu}
                  headerCell={HeaderCell}
                />
              ))}
            </Grid>
          </ExcelExport>
        </Main>
      )}
    </ContentLayout>
  );
};

// @ts-ignore
const mapStateToProps = ({ authentication }) => ({ authentication });

export default connect(mapStateToProps, null)(ViewReport);
